import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { Auth } from '../auth/auth.service';
import { HabilitationDto, HabilitationLiberalDto } from '../../models/habilitation-dto.model';
import { HabilitationType } from '../../models/habilitation-type.model';
import { Permission } from '../../models/permission.model';
import { PersonnePhysiqueDto } from '../../models/personne-physique-dto.model';
import { RoleMetier } from '../../models/role-metier-dto.model';
import { ARSDto, StructureDto } from '../../models/structure-dto.model';
import { StructureService } from '../api/structure.service';
import { StructureTiers } from '../../models/structure.model';
import AuthRoles from '../../../assets/data/authRoles.json';
import { ARSCacheService } from '../ars-cache.service.ts/ars-cache.service';

export interface UserInStorage {
  userId: string;
  email: string;
  displayName: string;
  token: string;
  roles: string[];
  autorisations: string[];
}

export interface LoginInfoInStorage {
  success: boolean;
  message: string;
  landingPage: string;
  user?: UserInStorage;
  status?: string;
}

@Injectable()
export class UserInfoService {

  private readonly _connected = new BehaviorSubject<boolean>(undefined);
  private readonly storedHabilitationsSubject = new BehaviorSubject<boolean>(false);
  public currentUserKey = 'currentUser';
  public authKey = 'auth';
  public habilitationAuthKey = 'habilitationsAuth';
  public habilitationKey = 'habilitations';
  public personneKey = 'personnes';
  public cookiesAcceptedKey = 'cookies_accepted';
  public storage: Storage = sessionStorage; /* <--- you may switch between sessionStorage or LocalStrage (only one place to change)*/
  private canSeeCollaboratorsView: Observable<boolean>;


  constructor(private readonly structureService: StructureService, private arsCacheService: ARSCacheService) {}

  /**
   * Store userinfo from session storage
   */
  storeUserInfo(userInfoString: string) {
    this.storage.setItem(this.currentUserKey, userInfoString);
  }

  /**
   * Remove userinfo from session storage
   */
  removeUserInfo() {
    this.storage.removeItem(this.currentUserKey);
  }

  /**
   * Get userinfo from session storage
   */
  getUserInfo(): UserInStorage | null {
    try {
      const userInfoString: string = this.storage.getItem(this.currentUserKey);
      if (userInfoString) {
        return JSON.parse(this.storage.getItem(this.currentUserKey));
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  }

  isLoggedIn(): boolean {
    if (this.storage.getItem(this.currentUserKey)) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Get User's Display name from session storage
   */
  getUserName(): string {
    const userObj: UserInStorage = this.getUserInfo();
    if (userObj !== null) {
      return userObj.displayName;
    }
    return 'no-user';
  }

  getStoredToken(): string | null {
    const userObj: UserInStorage = this.getUserInfo();
    if (userObj !== null) {
      return userObj.token;
    }
    return null;
  }

  /**
   * Store userinfo from session storage
   */
  storeAuth(auth: Auth) {
    this.storage.setItem(this.authKey, JSON.stringify(auth));
    this._connected.next(true);
  }

  /**
   * Remove userinfo from session storage
   */
  removeAuth() {
    this.storage.removeItem(this.authKey);
    this._connected.next(false);
  }

  /**
   * Get userinfo from session storage
   */
  getAuth(): Auth | null {
    try {
      const authString: string = this.storage.getItem(this.authKey);
      if (authString) {
        return JSON.parse(authString);
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  }

  getAuthAutorisations(): string[] | null {
    const userObj: Auth = this.getAuth();
    if (userObj !== null) {
      return userObj.item.autorisations;
    }
    return null;
  }

  getAuthRoles(): string[] | null {
    const userObj: Auth = this.getAuth();
    if (userObj !== null) {
      return userObj.item.roles;
    }
    return null;
  }

  /**
   * Store userinfo from filtered Habilitations session storage
   * @param habilitation
   */
  storeHabilitationsAuth(habilitation: HabilitationDto[]) {
    this.storage.setItem(this.habilitationAuthKey, JSON.stringify(habilitation));
  }

  /**
   * Store userinfo of ALL habilitations from session storage
   * @param habilitation
   */
  storeHabilitations(habilitation: HabilitationLiberalDto[]) {
    this.storage.setItem(this.habilitationKey, JSON.stringify(habilitation));
    this.checkCollaborateurPermission();
  }

  /**
   * Remove userinfo from session storage
   */
  removeHabilitations() {
    this.storage.removeItem(this.habilitationAuthKey);
  }

  /**
   * Get userinfo of Filtered Habilitations from session storage
   */
  getHabilitationsAuth(): HabilitationDto[] | null {
    try {
      const habilitationString: string = this.storage.getItem(this.habilitationAuthKey);
      return (habilitationString) ? JSON.parse(habilitationString) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Get userinfo of ALL Habilitations from session storage
   */
  getHabilitations(): HabilitationLiberalDto[] | null {
    try {
      const habilitationString: string = this.storage.getItem(this.habilitationKey);
      return (habilitationString) ? JSON.parse(habilitationString) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Stocker si les cookies ont été acceptés dans le stockage de session
   */
  storeCookiesAccepted() {
    this.storage.setItem(this.cookiesAcceptedKey, JSON.stringify('true'));
  }

  /**
   * Obtenir les informations si les cookies ont été acceptés à partir du stockage de session
   * @returns
   */
  getCookiesAccepted(): boolean {
    try {
      const cookiesString: string = this.storage.getItem(this.cookiesAcceptedKey);
      return cookiesString.includes('true');
    } catch (e) {
      return false;
    }
  }

  /**
   * Store userinfo from session storage
   * @param personne
   */
  storePersonnes(personne: PersonnePhysiqueDto) {
    this.storage.setItem(this.personneKey, JSON.stringify(personne));
    this._connected.next(true);
  }

  /**
   * Remove userinfo from session storage
   */
  removePersonnes() {
    this.storage.removeItem(this.personneKey);
  }

  /**
   * Get userinfo from session storage
   */
  getPersonnes(): PersonnePhysiqueDto | null {
    try {
      const personnesString: string = this.storage.getItem(this.personneKey);
      return (personnesString) ? JSON.parse(personnesString) : null;
    } catch (e) {
      return null;
    }
  }

  get connected(): any {
    return this._connected;
  }

  getPermissions(permissions: string): boolean {
    const habilitations = this.getHabilitationsAuth();
    if (
      habilitations === null ||
      habilitations.length === 0 ||
      !this.getAuthRoles().some((r?) => AuthRoles.includes(r))
    ) {
      return false;
    }

    switch (permissions) {
      case Permission.CL:
        return true;
      case Permission.DELEGUE:
      case Permission.GAE:
      case Permission.MANDATAIRE:
      case Permission.REPRESENTANT_LEGAL:
      case Permission.DELEGUE_GENERIQUE:
      case Permission.ADMIN_GENERIQUE:
        return this.getAuthRoles().includes('MANDATAIRE') || this.getAuthRoles().includes('REPRESENTANT_LEGAL');
      case Permission.CL_PM:
        return habilitations.some((hab) => hab.structure?.structureTiers === 'NON' &&
          ([HabilitationType.MANDATAIRE, HabilitationType.REPRESENTANT_LEGAL, HabilitationType.DELEGUE]
            .includes(hab.roleMetier?.code as HabilitationType)));
      case Permission.DELEGUE_PM:
        return habilitations.some((hab) => hab.structure?.structureTiers === 'NON' &&
          ([HabilitationType.MANDATAIRE, HabilitationType.REPRESENTANT_LEGAL]
            .includes(hab.roleMetier?.code as HabilitationType)));
      case Permission.GARS:
        return this.arsCacheService.existeStructureARSAppartenantUtilisateur(habilitations);
      default:
        return false;
    }
  }

  /**
   * Check if a user can see collaborators view or actions.
   * @returns True if a user has at list one habilitation as a structure tiers else false.
   */
  canHandleCollaborateurs(): Observable<boolean> {
    if (this.canSeeCollaboratorsView !== undefined) {
      return this.canSeeCollaboratorsView;
    } else {
      this.checkCollaborateurPermission();
      return this.canHandleCollaborateurs();
    }
  }

  private checkCollaborateurPermission() {
    const idNatStructList = this.getHabilitations()
      ?.filter(habilitation => [RoleMetier.MANDATAIRE.toString(), RoleMetier.REPRESENTANT_LEGAL.toString()].includes(habilitation.profil))
      .map(habilitation => habilitation.structure.idNatStruct) || [];

    this.canSeeCollaboratorsView = idNatStructList.length > 0 ?
      this.structureService.getByListIdStructure(idNatStructList)
        .pipe(
          map((structuresMap: Map<string, StructureDto>) =>
            Object.values(structuresMap).some((structure: StructureDto) => structure.structureTiers === StructureTiers.OUI))
        ) :
      of(false);
  }

  getStoredHabilitationsUpdatedEvent(): Observable<boolean> {
    return this.storedHabilitationsSubject.asObservable();
  }

  /**
   Envoie un évènement au composant observateur afin d'indiquer que les habilitations stockées ont été
   mises à jour.
  */
  pushStoredHabilitationsUpdatedEvent() {
    this.storedHabilitationsSubject.next(true);
  }

}
